import { Button, Panel, List, FlexboxGrid, Text, Tag, Form, Input, Message, Modal } from "rsuite";
import { FormEvent, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { bindActionCreators } from "redux";
import Header from "../layouts/Header";
import PageBase from "../components/PageBase";
import { actionCreators } from "../state";
import { authUserData } from "../service";
import { changePassword, deleteAccount } from "../service/AuthService";

export default function UserProfile() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { updateUser } = bindActionCreators(actionCreators, dispatch);
  const user = authUserData();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordMessage, setPasswordMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);


  const header = <Header HeaderTitle="會員資料" isShowRefresh={false} OnReturn={() => { navigate("/rider") }} OnRefresh={() => { }}></Header>;

  const handleEditEmail = () => {
    navigate("/email-verification");
  };

  const handleLogout = () => {
    updateUser(null);
    navigate("/");
  };

  const handleChangePassword = async (_formValue: Record<string, any> | null, event?: FormEvent<HTMLFormElement>) => {
    event?.preventDefault();
    setPasswordMessage(null);

    if (!user?.jwtToken) {
      setPasswordMessage({ type: "error", text: "找不到登入資訊，請重新登入後再試。" });
      return;
    }

    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordMessage({ type: "error", text: "請完整填寫所有欄位。" });
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordMessage({ type: "error", text: "新密碼與確認新密碼不一致。" });
      return;
    }

    if (newPassword.length < 8) {
      setPasswordMessage({ type: "error", text: "新密碼至少需 8 碼。" });
      return;
    }

    try {
      setIsChangingPassword(true);
      const result = await changePassword({ currentPassword, newPassword }, user.jwtToken);
      if (!result || result.statusCode !== 1) {
        setPasswordMessage({ type: "error", text: result?.message || "更新密碼失敗，請稍後再試。" });
        return;
      }

      setPasswordMessage({ type: "success", text: "密碼已成功更新。" });
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (error) {
      console.error("change password failed", error);
      setPasswordMessage({ type: "error", text: "更新密碼時發生錯誤，請稍後再試。" });
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!user?.jwtToken) {
      setDeleteError("找不到登入資訊，請重新登入後再試。");
      return;
    }

    try {
      setIsDeletingAccount(true);
      setDeleteError(null);
      const result = await deleteAccount(user.jwtToken);
      if (!result || result.statusCode !== 1) {
        setDeleteError(result?.message || "刪除帳號失敗，請稍後再試。");
        return;
      }

      // Successfully deleted - log out and redirect
      setShowDeleteModal(false);
      updateUser(null);
      navigate("/");
    } catch (error) {
      console.error("delete account failed", error);
      setDeleteError("刪除帳號時發生錯誤，請稍後再試。");
    } finally {
      setIsDeletingAccount(false);
    }
  };


  return (<PageBase header={header} content={<>
    {user ? <>
      <br />
      <Panel bordered style={{ margin: '10px', backgroundColor: '#f7f7fa' }}>
        <h4 style={{ marginBottom: '15px' }}>個人資料</h4>
        <List>
          <List.Item>
            <FlexboxGrid>
              <FlexboxGrid.Item colspan={6}>
                <Text weight="semibold">使用者ID:</Text>
              </FlexboxGrid.Item>
              <FlexboxGrid.Item colspan={18}>
                <Text style={{ textAlign: "left" }}>{user.id}</Text>
              </FlexboxGrid.Item>
            </FlexboxGrid>
          </List.Item>

          <List.Item>
            <FlexboxGrid>
              <FlexboxGrid.Item colspan={6}>
                <Text weight="semibold">顯示名稱:</Text>
              </FlexboxGrid.Item>
              <FlexboxGrid.Item colspan={18}>
                <Text style={{ textAlign: "left" }}>{user.displayName}</Text>
              </FlexboxGrid.Item>
            </FlexboxGrid>
          </List.Item>
          <List.Item>
            <FlexboxGrid>
              <FlexboxGrid.Item colspan={6}>
                <Text weight="semibold">帳號:</Text>
              </FlexboxGrid.Item>
              <FlexboxGrid.Item colspan={18}>
                <Text style={{ textAlign: "left" }}>
                  {user.authProvider === 'LINE' || user.authProvider === 'APPLE'
                    ? '已使用 LINE / Apple 登入'
                    : user.userId}
                </Text>
              </FlexboxGrid.Item>
            </FlexboxGrid>
          </List.Item>

          <List.Item>
            <FlexboxGrid>
              <FlexboxGrid.Item colspan={6}>
                <Text weight="semibold">電子郵件:</Text>
              </FlexboxGrid.Item>
              <FlexboxGrid.Item colspan={12}>
                <Text style={{ textAlign: "left" }}>{user.email || '未設定'}</Text>
              </FlexboxGrid.Item>
              <FlexboxGrid.Item colspan={6}>
                <Button
                  size="xs"
                  appearance="link"
                  color="blue"
                  onClick={handleEditEmail}
                >
                  編輯
                </Button>
              </FlexboxGrid.Item>
            </FlexboxGrid>
          </List.Item>

          <List.Item>
            <FlexboxGrid>
              <FlexboxGrid.Item colspan={6}>
                <Text weight="semibold">角色:</Text>
              </FlexboxGrid.Item>
              <FlexboxGrid.Item colspan={18} style={{ textAlign: "left" }}>
                {user.roleString.split(',').map((role: string, idx: number) => (
                  <Tag key={idx} color="blue" style={{ marginRight: 4 }}>{role.trim()}</Tag>
                ))}
              </FlexboxGrid.Item>
            </FlexboxGrid>
          </List.Item>

          {user.team && (
            <List.Item>
              <FlexboxGrid>
                <FlexboxGrid.Item colspan={6}>
                  <Text weight="semibold">團隊:</Text>
                </FlexboxGrid.Item>
                <FlexboxGrid.Item colspan={12}>
                  <Text style={{ textAlign: "left" }}>{user.team.name} ({user.team.code})</Text>
                </FlexboxGrid.Item>
                <FlexboxGrid.Item colspan={6}>
                  {user.roleString.includes('Lead') &&
                    <Button size="xs" appearance="link" color="blue" onClick={() => navigate(`/admin`)}>管理</Button>
                  }
                </FlexboxGrid.Item>
              </FlexboxGrid>
            </List.Item>
          )}
        </List>
      </Panel>

      <Panel bordered style={{ margin: '10px', backgroundColor: '#f7f7fa' }}>
        <h4 style={{ marginBottom: '15px' }}>變更密碼</h4>
        {user?.authProvider !== 'LINE' && user?.authProvider !== 'APPLE' ? (
          <>
            <Form fluid onSubmit={handleChangePassword}>
              <Form.Group>
                <Form.ControlLabel>目前密碼</Form.ControlLabel>
                <Input
                  type="password"
                  autoComplete="current-password"
                  value={currentPassword}
                  onChange={setCurrentPassword}
                  placeholder="輸入目前密碼"
                />
              </Form.Group>
              <Form.Group>
                <Form.ControlLabel>新密碼</Form.ControlLabel>
                <Input
                  type="password"
                  autoComplete="new-password"
                  value={newPassword}
                  onChange={setNewPassword}
                  placeholder="至少 8 碼"
                />
              </Form.Group>
              <Form.Group>
                <Form.ControlLabel>確認新密碼</Form.ControlLabel>
                <Input
                  type="password"
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={setConfirmPassword}
                  placeholder="再次輸入新密碼"
                />
              </Form.Group>
              {passwordMessage && (
                <Message showIcon type={passwordMessage.type} style={{ textAlign: "left", marginBottom: 12 }}>
                  {passwordMessage.text}
                </Message>
              )}
              <Button appearance="primary" loading={isChangingPassword} disabled={isChangingPassword} type="submit">
                更新密碼
              </Button>
            </Form>
          </>
        ) : (
          <Message type="info" style={{ textAlign: "left" }}>
            您是以 LINE 或 Apple 登入，若需設定傳統密碼，請先透過客服建立帳號。
          </Message>
        )}
      </Panel>

      <div style={{ margin: '20px 10px', textAlign: 'center' }}>
        <Button
          color="red"
          appearance="primary"
          size="md"
          onClick={handleLogout}
          block
        >
          登出
        </Button>
      </div>

      <Panel bordered style={{ margin: '10px', backgroundColor: '#fff5f5' }}>
        <h4 style={{ marginBottom: '15px', color: '#c00' }}>刪除帳號</h4>
        <Text style={{ marginBottom: '15px', textAlign: 'left' }}>
          刪除帳號後，您的所有資料將被永久移除，包括訂單記錄、司機資料等，且無法恢復。
        </Text>
        <Button
          color="red"
          appearance="ghost"
          size="md"
          onClick={() => setShowDeleteModal(true)}
          block
        >
          刪除我的帳號
        </Button>
      </Panel>

      <Modal open={showDeleteModal} onClose={() => setShowDeleteModal(false)} size="xs">
        <Modal.Header>
          <Modal.Title>確認刪除帳號</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Text style={{ marginBottom: '10px' }}>
            您確定要刪除帳號嗎？此操作無法復原。
          </Text>
          <Text weight="bold" style={{ color: '#c00' }}>
            所有資料將被永久刪除，包括：
          </Text>
          <ul style={{ textAlign: 'left', marginTop: '10px', paddingLeft: '20px' }}>
            <li>個人資料</li>
            <li>訂單記錄</li>
            {user?.driver && <li>司機資料與車輛資訊</li>}
          </ul>
          {deleteError && (
            <Message showIcon type="error" style={{ marginTop: '15px' }}>
              {deleteError}
            </Message>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            onClick={() => setShowDeleteModal(false)}
            appearance="subtle"
            disabled={isDeletingAccount}
          >
            取消
          </Button>
          <Button
            onClick={handleDeleteAccount}
            color="red"
            appearance="primary"
            loading={isDeletingAccount}
            disabled={isDeletingAccount}
          >
            確認刪除
          </Button>
        </Modal.Footer>
      </Modal>

    </> : <>
      <br />
      <span>尚未登入</span>
    </>}
  </>} showSide />);
}
